// A curated list of *public* (no-auth) data sources that can power Legal Listings & Public Notices.
// Many state/county notice portals do not offer a unified API; for those we expose "official directory" links
// and leave room for per-jurisdiction adapters later.

export const LEGAL_LISTING_CATEGORIES = [
  {
    key: "public",
    title: "Public",
    description: "General public notices and announcements.",
  },
  {
    key: "commercial",
    title: "Commercial",
    description: "Commercial/legal-business notices (UCC, procurement, etc.).",
  },
  {
    key: "court",
    title: "Court Order",
    description: "Court orders, court calendars, docket / order references.",
  },
  {
    key: "fictitious",
    title: "Fictitious",
    description: "DBA/fictitious name notices and related publications.",
  },
  {
    key: "announcement",
    title: "Announcement",
    description: "Community announcements, event notices, media notices.",
  },
  {
    key: "legal",
    title: "Legal Listing",
    description: "General legal listings (foreclosures, auctions, liens, etc.).",
  },
  {
    key: "legalSupport",
    title: "Legal Support",
    description:
      "Support listings to help people navigate legal needs (attorneys, bail bonds, guidance).",
  },
  {
    key: "tourism",
    title: "Tourist / Travel",
    description: "Travel, tours & guides, attractions listings & links.",
  },
  {
    key: "agriculture",
    title: "Rural / Agriculture",
    description: "Rural areas, agriculture opportunities, agritourism.",
  },
  {
    key: "surplus",
    title: "Surplus Properties",
    description: "Surplus properties listing advertisements.",
  },
  {
    key: "markets",
    title: "Flea Markets / Auctions",
    description: "Flea markets, trading & auctions.",
  },
  {
    key: "mediaSecurity",
    title: "Media / Production / Security",
    description: "Media, production, and security training listings.",
  },
  {
    key: "recycling",
    title: "Recycling Venues",
    description: "Recycling venues and related services.",
  },
  {
    key: "minorityProducts",
    title: "Minority Products",
    description: "Minority products listings.",
  },
  {
    key: "languageClasses",
    title: "Language Classes",
    description: "Language classes (English & Spanish).",
  },
  {
    key: "petServices",
    title: "Pet Services",
    description:
      "Pet adoption centers, insurance, hospitals, parks, hotels, and stores.",
  },
];

// Each source can be shown to users immediately as a "connectable" provider.
// "adapter": a future code module name to implement a true data pull.
export const LEGAL_SOURCES = [
  {
    id: "fedregister",
    name: "Federal Register (US)",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "commercial", "court", "announcement", "legal"],
    docsUrl: "https://www.federalregister.gov/developers/api/v1",
    adapter: "federalregister",
  },
  {
    id: "govinfo",
    name: "govinfo (GPO)",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "commercial", "court", "legal"],
    // Use the human-facing developer hub (avoid opening the raw API root).
    docsUrl: "https://www.govinfo.gov/developers",
    adapter: "govinfo",
    note: "Requires an api.data.gov key (free signup) to use api.govinfo.gov.",
  },
  {
    id: "samgov",
    name: "SAM.gov (Contract Opportunities)",
    coverage: "federal",
    type: "api",
    categoryKeys: ["commercial"],
    docsUrl: "https://open.gsa.gov/api/get-opportunities-public-api/",
    adapter: "samgov",
    note: "API key required (GSA).",
  },
  {
    id: "edgar",
    name: "SEC EDGAR",
    coverage: "federal",
    type: "api",
    categoryKeys: ["commercial", "legal"],
    docsUrl: "https://www.sec.gov/developer",
    adapter: "sec-edgar",
    note: "Requires compliant User-Agent and rate limits.",
  },

  // --- U.S.-wide directories (good default fallbacks) ---
  {
    id: "usa-gov-state",
    name: "USA.gov — State government directory",
    coverage: "national",
    type: "directory",
    categoryKeys: [
      "public",
      "commercial",
      "court",
      "fictitious",
      "announcement",
      "legal",
      "legalSupport",
      "tourism",
      "agriculture",
      "surplus",
      "markets",
      "mediaSecurity",
      "recycling",
      "minorityProducts",
      "languageClasses",
      "petServices",
    ],
    docsUrl: "https://www.usa.gov/state-governments",
    adapter: "link",
    note: "Best starting point to find each state's official sites (courts, agencies, SOS, etc.).",
  },
  {
    id: "usa-gov-local",
    name: "USA.gov — Local government directory",
    coverage: "national",
    type: "directory",
    categoryKeys: [
      "public",
      "commercial",
      "court",
      "fictitious",
      "announcement",
      "legal",
      "legalSupport",
      "tourism",
      "agriculture",
      "surplus",
      "markets",
      "mediaSecurity",
      "recycling",
      "minorityProducts",
      "languageClasses",
      "petServices",
    ],
    docsUrl: "https://www.usa.gov/local-governments",
    adapter: "link",
    note: "Find city/county government entry points when county-level data is required.",
  },

  // --- Public data APIs (no keys or optional keys) ---
  {
    id: "federal-register",
    name: "Federal Register API",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "announcement", "legal"],
    docsUrl: "https://www.federalregister.gov/developers/documentation/api/v1",
    adapter: "federal-register",
    note: "Open API for federal notices/rules. Useful as a live, nation-wide feed.",
  },
  {
    id: "data-gov",
    name: "data.gov (dataset catalog)",
    coverage: "national",
    type: "api",
    categoryKeys: [
      "public",
      "commercial",
      "legal",
      "legalSupport",
      "tourism",
      "agriculture",
      "surplus",
      "markets",
      "mediaSecurity",
      "recycling",
      "minorityProducts",
      "languageClasses",
      "petServices",
    ],
    // Point to human docs (avoid opening the raw JSON endpoint)
    docsUrl: "https://data.gov/developers/apis/",
    adapter: "data-gov-catalog",
    note: "Searchable catalog of U.S. public datasets (federal + some state/local).",
  },
  {
    id: "regulations-gov",
    name: "Regulations.gov API",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "announcement", "legal"],
    docsUrl: "https://open.gsa.gov/api/regulationsgov/",
    adapter: "regulations-gov",
    note: "Federal rulemaking dockets & documents. Typically needs an API key.",
  },
  {
    id: "usaspending",
    name: "USAspending.gov API",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "commercial"],
    docsUrl: "https://api.usaspending.gov/docs/",
    adapter: "usaspending",
    note: "Open API for federal spending. Helpful for commercial/public context.",
  },
  {
    id: "openfec",
    name: "OpenFEC API",
    coverage: "federal",
    type: "api",
    categoryKeys: ["public", "legal"],
    docsUrl: "https://api.open.fec.gov/developers/",
    adapter: "openfec",
    note: "Campaign finance data (requires API key).",
  },
  {
    id: "uspto",
    name: "USPTO Open Data (Patents/Trademarks)",
    coverage: "federal",
    type: "api",
    categoryKeys: ["commercial", "legal"],
    docsUrl: "https://developer.uspto.gov/",
    adapter: "uspto",
    note: "IP-related public data; useful for commercial/legal listings.",
  },

  {
    id: "state_official_portals",
    name: "State / County Official Portals",
    coverage: "state+county",
    type: "directory",
    categoryKeys: [
      "public",
      "commercial",
      "court",
      "fictitious",
      "announcement",
      "legal",
      "legalSupport",
      "tourism",
      "agriculture",
      "surplus",
      "markets",
      "mediaSecurity",
      "recycling",
      "minorityProducts",
      "languageClasses",
      "petServices",
    ],
    docsUrl: "",
    adapter: "state-directory",
    note:
      "Many jurisdictions publish notices on official sites or approved newspapers with no unified API. We provide official-link directories and can add adapters state-by-state.",
  },
  {
    id: "pacer",
    name: "PACER (Federal Courts)",
    coverage: "federal",
    type: "restricted",
    categoryKeys: ["court"],
    docsUrl: "https://pacer.uscourts.gov/",
    adapter: "pacer",
    note: "Paid access; integration typically requires credentials and compliance.",
  },
];


export function buildSourceLinks({ state, countyFips, categoryKey }) {
  const linksBySource = {};
  const stateName = state?.name || "";
  const stateAbbr = state?.abbr || "";

  const USA_GOV_STATE_DIR = "https://www.usa.gov/state-governments";
  const USA_GOV_LOCAL_DIR = "https://www.usa.gov/local-governments";
  const USA_GOV_STATES_ROOT = "https://www.usa.gov/states";

  const toUsaGovStateSlug = (name) => {
    const n = String(name || "").trim().toLowerCase();
    if (!n) return "";
    if (n === "district of columbia") return "district-of-columbia";
    return n
      .replace(/\./g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  const pushUnique = (arr, item) => {
    if (!item?.href) return;
    if (arr.some((x) => x.href === item.href)) return;
    arr.push(item);
  };

  for (const s of LEGAL_SOURCES) {
    const arr = [];

    // 1) Put the most user-friendly landing/search pages FIRST
    if (s.id === "govinfo") {
      pushUnique(arr, { label: "Search", href: "https://www.govinfo.gov/app/search/" });
      pushUnique(arr, { label: "API key signup", href: "https://www.govinfo.gov/api-signup", dev: true });
    }

    if (s.id === "fedregister" || s.id === "federal-register") {
      pushUnique(arr, { label: "Search", href: "https://www.federalregister.gov/documents/search" });
    }

    if (s.id === "data-gov") {
      pushUnique(arr, { label: "Catalog", href: "https://catalog.data.gov/" });
    }

    if (s.id === "usaspending") {
      pushUnique(arr, { label: "Search", href: "https://www.usaspending.gov/search" });
    }

    if (s.id === "openfec") {
      pushUnique(arr, { label: "FEC data", href: "https://www.fec.gov/data/" });
    }

    if (s.id === "samgov") {
      pushUnique(arr, { label: "Search", href: "https://sam.gov/opportunities" });
    }

    if (s.id === "regulations-gov") {
      pushUnique(arr, { label: "Regulations.gov", href: "https://www.regulations.gov/" });
    }

    if (s.id === "edgar") {
      pushUnique(arr, { label: "EDGAR search", href: "https://www.sec.gov/edgar/search" });
    }

    if (s.id === "uspto") {
      pushUnique(arr, { label: "USPTO", href: "https://www.uspto.gov/" });
    }

    // 2) Directories with state-specific deep links
    if (s.id === "usa-gov-state") {
      const slug = toUsaGovStateSlug(stateName);
      if (slug) {
        pushUnique(arr, { label: `${stateName} official sites`, href: `${USA_GOV_STATES_ROOT}/${slug}` });
      }
      pushUnique(arr, { label: "State governments", href: USA_GOV_STATE_DIR });
    }

    if (s.id === "usa-gov-local") {
      pushUnique(arr, { label: stateAbbr ? `Local directory (${stateAbbr})` : "Local directory", href: USA_GOV_LOCAL_DIR });
    }
    if (s.id === "state_official_portals") {
      const slug = toUsaGovStateSlug(stateName);
      if (slug) {
        pushUnique(arr, { label: `${stateName} official sites`, href: `${USA_GOV_STATES_ROOT}/${slug}` });
      }
      pushUnique(arr, { label: "State directory", href: USA_GOV_STATE_DIR });
      pushUnique(arr, { label: "Local directory", href: USA_GOV_LOCAL_DIR });
      pushUnique(arr, { label: "Find a county", href: USA_GOV_LOCAL_DIR });
      if (stateName) {
        pushUnique(arr, { label: `Add ${stateName} portal adapter`, href: USA_GOV_STATE_DIR });
      }
    }
    // 3) Then include official docs link (hidden unless Developer links are enabled)
    if (s.docsUrl) {
      if (s.type === "api") {
        pushUnique(arr, { label: "API docs", href: s.docsUrl, dev: true });
      } else {
        pushUnique(arr, { label: "Open", href: s.docsUrl });
      }
    }


    // Keep these params for future routing.
    // eslint-disable-next-line no-unused-vars
    const _meta = { countyFips, categoryKey };

    linksBySource[s.id] = arr;
  }

  return linksBySource;
}
